import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const EVENT = CartAddEvent.eventName;

/**
 * @param {unknown} cents
 * @returns {string}
 */
function formatCartCents(cents) {
  if (cents == null || cents === '') return '';
  const n = Number(cents);
  if (Number.isNaN(n)) return '';
  const t = typeof window !== 'undefined' ? window.Theme : undefined;
  const format = t?.moneyFormat || '{{amount}}';
  const currency = t?.currency || 'USD';
  return formatMoney(n, format, currency);
}

/**
 * @param {HTMLElement} priceEl
 * @param {Record<string, unknown>} lineItem
 */
function setLineItemPrice(priceEl, lineItem) {
  const unitCents = Number(lineItem?.price);
  const compareRaw = lineItem?.compare_at_price;
  const compareCents = compareRaw != null && compareRaw !== '' ? Number(compareRaw) : NaN;

  const hasValidUnit = !Number.isNaN(unitCents);
  const showCompare =
    hasValidUnit && !Number.isNaN(compareCents) && compareCents > unitCents;

  if (showCompare) {
    priceEl.innerHTML = `<span class="cart-add-toast__compare">${formatCartCents(compareCents)}</span><span class="cart-add-toast__price-current">${formatCartCents(unitCents)}</span>`;
  } else if (hasValidUnit) {
    priceEl.textContent = formatCartCents(unitCents);
  } else {
    priceEl.textContent = '';
  }
}

function escapeHtml(s) {
  if (s == null || s === '') return '';
  const d = document.createElement('div');
  d.textContent = String(s);
  return d.innerHTML;
}

function createOverlay() {
  const el = document.createElement('div');
  el.id = 'cart-add-toast-overlay';
  el.className = 'cart-add-toast-overlay';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

function createToastHtml() {
  const container = document.createElement('div');
  container.id = 'cart-add-toast';
  container.className = 'cart-add-toast';
  container.setAttribute('role', 'dialog');
  container.setAttribute('aria-modal', 'true');
  container.setAttribute('aria-labelledby', 'cart-add-toast-heading');
  container.innerHTML = `
    <div class="cart-add-toast__inner">
      <button type="button" class="cart-add-toast__close" aria-label="Close">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="cart-add-toast__header">
        <img class="cart-add-toast__header-icon" src="https://cdn.shopify.com/s/files/1/0932/2476/3675/files/tick-circle.svg?v=1769326065" alt="" />
        <div id="cart-add-toast-heading" class="cart-add-toast__header-text">Added to your cart!</div>
      </div>
      <div class="cart-add-toast__body">
        <div class="cart-add-toast__media"><img src="" alt=""/></div>
        <div class="cart-add-toast__meta">
          <div class="cart-add-toast__title"></div>
          <div class="cart-add-toast__details"></div>
          <div class="cart-add-toast__price"></div>
        </div>
      </div>
      <div class="cart-add-toast__actions">
        <a href="/cart" class="button cart-add-toast__view">VIEW CART</a>
        <a href="/checkout" class="button button-ghost cart-add-toast__checkout">CHECKOUT</a>
      </div>
    </div>
  `;
  return container;
}

function injectStyles() {
  if (document.getElementById('cart-add-toast-styles')) return;
  const style = document.createElement('style');
  style.id = 'cart-add-toast-styles';
  style.textContent = `
    .cart-add-toast-overlay {
      position: fixed;
      inset: 0;
      z-index: 1190;
      background: rgb(0 0 0 / 45%);
      display: none;
      -webkit-tap-highlight-color: transparent;
    }
    .cart-add-toast-overlay--visible {
      display: block;
    }
    body.cart-add-toast--open {
      overflow: hidden;
    }
    .cart-add-toast {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      top: auto;
      z-index: 1200;
      width: 100%;
      max-width: 100%;
      margin: 0;
      background: #fff;
      box-shadow: 0 -8px 40px rgb(0 0 0 / 12%);
      border-radius: 12px 12px 0 0;
      overflow: visible;
      display: none;
      transform: translateY(100%);
      box-sizing: border-box;
    }
    .cart-add-toast--visible {
      display: block;
      animation: cartAddToastSlideUp 0.38s cubic-bezier(0.32, 0.72, 0, 1) forwards;
    }
    @keyframes cartAddToastSlideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
    .cart-add-toast__inner {
      position: relative;
      padding: calc(20px + 25px) 16px 20px;
      box-sizing: border-box;
    }
    .cart-add-toast__close {
      position: absolute;
      top: 0;
      left: 50%;
      right: auto;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      border: none;
      background-color: #d8d8d8;
      color: #03045e;
      cursor: pointer;
      z-index: 3;
      box-shadow: none;
    }
    .cart-add-toast__close:active {
      transform: translate(-50%, -50%) scale(0.97);
    }
    .cart-add-toast__header {
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: #00a63026;
      border: 1px solid #dfeee0;
      margin-bottom: 12px;
    }
    .cart-add-toast__header-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }
    .cart-add-toast__header-text {
      font-family: 'Helvetica Neue', HelveticaNeueCustom, Helvetica, Arial, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0;
      color: #171717;
    }
    .cart-add-toast__body {
      display: flex;
      gap: 12px;
      align-items: flex-start;
    }
    .cart-add-toast__media {
      flex-shrink: 0;
    }
    .cart-add-toast__media img {
      border: 0.33px solid var(--Grey-Text, #a8a8a8);
      width: 72px;
      height: 72px;
      object-fit: contain;
      border-radius: 6px;
      display: block;
      margin: 0;
    }
    .cart-add-toast__meta {
      flex: 1;
      min-width: 0;
    }
    .cart-add-toast__title {
      font-family: 'Barlow Semi Condensed';

      font-size: 24px;
      line-height: 100%;
      letter-spacing: 0;
      text-transform: uppercase;
      color: #313131;
      margin: 0 0 8px;
    }
    .cart-add-toast__details {
      margin-bottom: 8px;
    }
    .cart-add-toast__option-row {
      font-size: 14px;
      line-height: 21px;
      letter-spacing: 0;
      margin-bottom: 2px;
    }
    .cart-add-toast__option-row:last-child {
      margin-bottom: 0;
    }
    .cart-add-toast__option-name {
      font-family: 'Helvetica Neue', HelveticaNeueCustom, Helvetica, Arial, sans-serif;
      font-weight: 400;
      color: #a8a8a8;
      margin-right: 4px;
    }
    .cart-add-toast__option-value {
      font-family: 'Helvetica Neue', HelveticaNeueCustom, Helvetica, Arial, sans-serif;
      font-weight: 400;
      color: #313131;
    }
    .cart-add-toast__price {
      font-family: 'Barlow Semi Condensed';
      font-weight: 700;
      font-size: 24px;
      line-height: 100%;
      letter-spacing: 0;
      text-transform: uppercase;
      color: #313131;
    }
    .cart-add-toast__compare {
      text-decoration: line-through;
      margin-right: 8px;
      color: #a8a8a8;
      font-family: 'Barlow Semi Condensed';
      font-weight: 700;
      font-size: 24px;
      line-height: 100%;
      text-transform: uppercase;
    }
    .cart-add-toast__price-current {
      font-family: 'Barlow Semi Condensed';
      font-weight: 700;
      font-size: 24px;
      line-height: 100%;
      letter-spacing: 0;
      text-transform: uppercase;
      color: #313131;
    }
    .cart-add-toast__actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 16px;
    }
    .cart-add-toast__view {
      font-weight: 500;
      font-size: 14px;
      line-height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 14px 18px;
      background: #03045e;
      color: #fff;
      border-radius: 8px;
      text-decoration: none;
      text-align: center;
      box-sizing: border-box;
    }
    .cart-add-toast__checkout {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 14px 18px;
      border-radius: 8px;
      text-decoration: none;
      border: 1.5px solid #03045e;
      color: #03045e;
      background: #fff;
      font-weight: 500;
      font-size: 14px;
      line-height: 100%;
      box-sizing: border-box;
    }
    @media screen and (min-width: 750px) {
      @keyframes cartAddToastSlideUpDesktop {
        from {
          transform: translateY(calc(100% + 20px));
        }
        to {
          transform: translateY(0);
        }
      }
      .cart-add-toast {
        left: auto;
        right: 20px;
        bottom: 20px;
        top: auto;
        width: 700px;
        max-width: calc(100vw - 40px);
        border-radius: 12px;
        box-shadow: 0 8px 40px rgb(0 0 0 / 14%);
        transform: translateY(calc(100% + 20px));
      }
      .cart-add-toast--visible {
        animation: cartAddToastSlideUpDesktop 0.38s cubic-bezier(0.32, 0.72, 0, 1) forwards;
      }
      .cart-add-toast__close {
        top: 20px;
        right: 20px;
        left: auto;
        transform: none;
        width: 40px;
        height: 40px;
        min-width: 40px;
        min-height: 40px;
        background: none;
        border: none;
      }
      .cart-add-toast__close:active {
        transform: scale(0.97);
      }
      .cart-add-toast__inner {
        padding: 24px;
      }
      .cart-add-toast__actions {
        flex-direction: row;
      }
      .cart-add-toast__view,
      .cart-add-toast__checkout {
        flex: 1 1 0;
        min-width: 0;
        width: auto;
      }
    }
  `;
  document.head.appendChild(style);
}

function populateToast(detail) {
  const toast = document.getElementById('cart-add-toast');
  if (!toast) return;
  const img = toast.querySelector('.cart-add-toast__media img');
  const title = toast.querySelector('.cart-add-toast__title');
  const detailsEl = toast.querySelector('.cart-add-toast__details');
  const priceEl = toast.querySelector('.cart-add-toast__price');

  let sourceEl = null;
  if (detail && detail.sourceId) {
    sourceEl =
      document.getElementById(detail.sourceId) ||
      document.querySelector(`#${CSS.escape(String(detail.sourceId))}`) ||
      null;
  }
  if (!sourceEl && detail && detail.resource && detail.resource.items && detail.resource.items.length > 0) {
    const last = detail.resource.items[detail.resource.items.length - 1];
    if (last && last.image) {
      img.src = last.image;
    }
    title.textContent = last?.title ?? 'Added to cart';

    const options = last?.options_with_values;
    if (options && Array.isArray(options) && options.length > 0) {
      detailsEl.innerHTML = options
        .map(
          (o) =>
            `<div class="cart-add-toast__option-row"><span class="cart-add-toast__option-name">${escapeHtml(o.name)} :</span><span class="cart-add-toast__option-value">${escapeHtml(o.value)}</span></div>`
        )
        .join('');
    } else {
      const variantText =
        last?.variant_title || (last.options && last.options.length ? last.options.join(' • ') : '');
      if (variantText) {
        detailsEl.innerHTML = `<div class="cart-add-toast__option-row"><span class="cart-add-toast__option-value">${escapeHtml(variantText)}</span></div>`;
      } else if (last?.properties && Object.keys(last.properties).length > 0) {
        detailsEl.innerHTML = Object.entries(last.properties)
          .map(
            ([k, v]) =>
              `<div class="cart-add-toast__option-row"><span class="cart-add-toast__option-name">${escapeHtml(k)} :</span><span class="cart-add-toast__option-value">${escapeHtml(String(v))}</span></div>`
          )
          .join('');
      } else {
        detailsEl.innerHTML = '';
      }
    }

    if (priceEl) setLineItemPrice(/** @type {HTMLElement} */ (priceEl), last);
    return;
  }

  if (sourceEl) {
    const productTitle = sourceEl.querySelector(
      '.product__title, .product-card__title, .product-card__heading, .card__heading, .product__name, .product-card__name, h1, h2, h3'
    );
    const productImg = sourceEl.querySelector('img');
    const variantEl = sourceEl.querySelector(
      '.variant__label, .product__variant, .product__subtitle, .product-card__variant'
    );

    img.src = (productImg && productImg.src) || Theme?.translations?.placeholder_image || '';
    title.textContent = (productTitle && productTitle.textContent.trim()) || 'Added to cart';
    const vt = variantEl && variantEl.textContent.trim();
    if (vt) {
      detailsEl.innerHTML = `<div class="cart-add-toast__option-row"><span class="cart-add-toast__option-value">${escapeHtml(vt)}</span></div>`;
    } else {
      detailsEl.innerHTML = '';
    }
    priceEl.textContent = '';
  } else {
    title.textContent = 'Added to cart';
    detailsEl.innerHTML = '';
    img.src = Theme?.translations?.placeholder_image || '';
    priceEl.textContent = '';
  }
}

function hideToast() {
  const toast = document.getElementById('cart-add-toast');
  const overlay = document.getElementById('cart-add-toast-overlay');
  if (toast) toast.classList.remove('cart-add-toast--visible');
  if (overlay) overlay.classList.remove('cart-add-toast-overlay--visible');
  document.body.classList.remove('cart-add-toast--open');
}

/**
 * @returns {boolean}
 */
function isCartDrawerOpen() {
  const dialog = document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

function bindToastUi(toast, overlay) {
  if (toast.dataset.cartToastBound === 'true') return;
  toast.dataset.cartToastBound = 'true';

  toast.querySelector('.cart-add-toast__close')?.addEventListener('click', hideToast);

  toast.querySelector('.cart-add-toast__view')?.addEventListener('click', (e) => {
    e.preventDefault();
    hideToast();
    requestAnimationFrame(() => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
    });
  });

  toast.querySelector('.cart-add-toast__checkout')?.addEventListener('click', () => {
    hideToast();
  });

  overlay.addEventListener('click', hideToast);
}

function showToast(detail) {
  injectStyles();

  let overlay = document.getElementById('cart-add-toast-overlay');
  if (!overlay) {
    overlay = createOverlay();
    document.body.appendChild(overlay);
  }

  let toast = document.getElementById('cart-add-toast');
  if (!toast) {
    toast = createToastHtml();
    document.body.appendChild(toast);
  }

  bindToastUi(toast, overlay);
  populateToast(detail);

  overlay.classList.add('cart-add-toast-overlay--visible');
  toast.classList.add('cart-add-toast--visible');
  document.body.classList.add('cart-add-toast--open');
}

document.addEventListener(
  EVENT,
  (event) => {
    if (event?.detail?.data?.__toastHandled) return;

    const didError = event?.detail?.data?.didError;
    if (didError) return;

    const src = event?.detail?.data?.source || '';
    if (src && /remove/i.test(src)) return;

    const itemCount = event?.detail?.data?.itemCount;
    if (typeof itemCount === 'number' && itemCount <= 0) return;

    if (isCartDrawerOpen()) return;

    try {
      const quick = document.getElementById('quick-add-dialog');
      if (quick && typeof quick.closeDialog === 'function') quick.closeDialog();
    } catch (e) {
      // ignore
    }

    showToast(event.detail);
  },
  false
);

export {};
